import {
  Component,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit, inject, DestroyRef,
} from '@angular/core';
import {Router, NavigationEnd, ChildActivationEnd} from '@angular/router';

import { filter, switchMap, distinctUntilChanged, map} from 'rxjs';
import {Subject, timer} from 'rxjs';

import { SettingsService } from '../app-shared/services/settings.service';
import { CartService } from '../app-shared/services/cart.service';
import { CatalogService } from '../catalog/catalog.service';
import { ProductsService } from '../catalog/products/products.service';
import { StorageService } from '../app-shared/services/storage.service';
import { GoogleAnalyticsService } from '../app-shared/services/google-analytics.service';
import { MainUserService } from '../app-shared/services/main-user.service';
import { MenuService } from './header/menu/menu.service';
import { AddNewCategoryDialogsComponent } from './add-new-category-dialogs/add-new-category-dialogs.component';
import { PermissionsService } from '../app-shared';
import {SelectedUserSuppliersAndOutlets} from "../app-shared/models/supplier-outlet.model";
import {PromoService} from "../promotions/promotions.service";
import {HomeService} from "../app-shared/services/home.service";
import {HeaderService} from "../app-shared/services/header.service";
import {FeedbackService} from "../feedback/feedback.service";
import {QuestionnaireDestinationType} from "../feedback/feedback.models";
import {InactivityService} from "../app-shared/services/inactivity.service";
import {UserNotificationService} from "./user-notification/user-notification.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import { throttleTime} from "rxjs/operators";
import {TabSessionService} from "../app-shared/services/tab-session.service";

@Component({
    selector: 'app-core',
    templateUrl: './core.component.html',
    styleUrls: ['./core.component.scss'],
    standalone: false
})
export class CoreComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollBlock') private scrollBlock: ElementRef;
  @ViewChild(AddNewCategoryDialogsComponent)
  addNewCategoryDialogsComponent: AddNewCategoryDialogsComponent;
  showWidgetCart = false;
  mobile: boolean;
  isHideFooter: boolean = false;
  showFeedbackPanel: boolean = false;
  feedbackSteps: any[] = [];
  hasFeedback: boolean = false;
  questionnaireId: number;
  showUserNotification: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  private scrollState$ = new Subject<number>();

  constructor(
    public settings: SettingsService,
    private cartService: CartService,
    public catalogService: CatalogService,
    private router: Router,
    private productsService: ProductsService,
    private storageService: StorageService,
    public gaService: GoogleAnalyticsService,
    private mainUserService: MainUserService,
    private menuService: MenuService,
    public permissionsService: PermissionsService,
    private promoService: PromoService,
    private homeService: HomeService,
    private feedbackService: FeedbackService,
    private headerService: HeaderService,
    private inactivityService: InactivityService,
    private userNotificationService: UserNotificationService,
    // need for init constructor TabSessionService
    private tabSessionService: TabSessionService,
  ) {

  }

  ngOnInit() {
    this.mobile = this.settings.isMobileSize();
    this.inactivityService.init(this.settings.config.inactivityDuration);
    this.checkHideFooter();
    this.checkChangeRoute();
    this.catalogService.isNonRegisteredUser = false;
    const isRegister = this.mainUserService.getUserData().isRegister;
    if (!isRegister) {
      this.router.navigate(['/activate-catalog']);
      return;
    }
    this.cartService.loadedProductDraft$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state) {
          this.catalogService.reloadCatalogForSelectedUserSupplierAndOutlet(
            () => {},
            () => {}
          );
        } else {
          this.catalogService.reloadCatalogForSelectedUserSupplierAndOutlet(
            () => {
              // this.recalculateCart();
              this.productsService.getDraftCartProducts();
            },
            () => {}
          );
        }
      });

    const selectedUserSuppliersAndOutlets = JSON.parse(
      this.storageService.getFromLocalStorage('selectedUserSuppliersAndOutlets')
    );
    this.gaService.eventEmitterGTM({
      event: 'initChat',
      chatName: `${selectedUserSuppliersAndOutlets?.outletName} ${selectedUserSuppliersAndOutlets?.supplierOutletDeliveryAddress}`,
      chatEmail: selectedUserSuppliersAndOutlets?.codeForChat,
      chatPhone: this.mainUserService.getUserData().phoneNumber,
    });

    this.checkChangeSupplierAndOutlets();
    this.getUserNotifications();
  }

  ngAfterViewInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        const numberPos = 0;
        this.scrollBlock.nativeElement.scrollTop = numberPos;
        this.showWidgetCart = false;
        this.checkHideFooter();
        this.checkShowFeedbackPanel();
      });
    this.handleScroll();
  }

  private checkChangeSupplierAndOutlets() {
    this.catalogService.changeSuppierAndOutlet
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.getPromotions();
        this.getBanners();
        this.getFeedback();
        if (this.mobile) {
          this.menuService.addDynamicMobileItemsMenu();
        }
      });
  }

  getPromotions() {
    const supAndOutlets: SelectedUserSuppliersAndOutlets = JSON.parse(
      this.storageService.getFromLocalStorage('selectedUserSuppliersAndOutlets')
    );
    this.promoService.getPromosByOutletId(supAndOutlets.supplierId, supAndOutlets.outletId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((promo) => {
        this.promoService.promoData.next(promo.promoActivities);
      });
  }

  getUserNotifications() {
    this.userNotificationService.getUserNotifications()
      timer(0, 1800000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.userNotificationService.getUserNotifications())
      )
      .subscribe((notifications) => {
        this.userNotificationService.notificationData.next(notifications);
      });
  }

  getBanners() {
    this.homeService
      .getBannerList(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (banners) => {
          this.homeService.bannerData.next(banners);
        }
      });
  }

  getFeedback() {
    const selectedUserSuppliersAndOutlets: SelectedUserSuppliersAndOutlets = JSON.parse(
      this.storageService.getFromLocalStorage('selectedUserSuppliersAndOutlets')
    );
    const supplierOutletId = selectedUserSuppliersAndOutlets.supplierOutletId;

    this.feedbackService.closePanel();

    this.feedbackService.getQuestionnaire(supplierOutletId, QuestionnaireDestinationType.General)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        let feedbackSteps = [];
        if (data && data.items && data.items.length > 0) {
          this.questionnaireId = data.id;
          feedbackSteps = this.feedbackService.processQuestionnaireData(data);
        }
        this.feedbackSteps = feedbackSteps;
        this.hasFeedback = feedbackSteps.length > 0;

        this.checkShowFeedbackPanel();
      });
  }

  checkChangeRoute() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof ChildActivationEnd) {
        this.checkHideFooter();
        this.checkShowFeedbackPanel();
      }
    });
  }

  checkHideFooter() {
    const currentUrl = this.router.url;
    this.isHideFooter = currentUrl.includes('catalog') ||
                        currentUrl.includes('cart') ||
                        (this.mobile && (currentUrl.includes('user/profile')) || (currentUrl.includes('orderhistory/orderhistorymobile')));  }

  checkShowFeedbackPanel() {
    this.showFeedbackPanel = this.hasFeedback && this.feedbackService.isShowFeedbackPanel(this.mobile);
  }

  handleScroll() {
    this.scrollState$
      .pipe(
        throttleTime(30, undefined, { leading: true, trailing: true }),
        map(top => top > 0), // true = hide, false = show
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(hide => {
        this.headerService.hideSearch.next(hide);
      });
  }

  hideHeader(event) {
    if (!this.mobile) return;
    const topPosition = event.target.scrollTop;
    this.scrollState$.next(topPosition);
  }

  ngOnDestroy() {
    this.permissionsService.setUserPermissionError('');
    this.catalogService.priceWithVatMode.next(true);
    this.cartService.productsInCartSubjectNewTotal.next(null);
    this.inactivityService.stop();
  }

  closeWidget(action) {
    this.showWidgetCart = false;
  }

  showWidget(action) {
    this.showWidgetCart = action;
  }

  addNewCategoryClick() {
    this.addNewCategoryDialogsComponent.addNewCategoryClick();
  }
}
